import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Phone, Mail, Linkedin, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';

const Contact = () => {
  const { toast } = useToast();
  const sectionsRef = useRef([]);
  const [result, setResult] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeIn');
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setResult('Sending...');

    const formDataToSend = new FormData(event.target);
    formDataToSend.append('access_key', '1d2f48e7-7b74-42f7-be3f-6bb6215d16dd');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formDataToSend,
      });

      const data = await response.json();

      if (data.success) {
        setResult('');
        toast({
          title: 'Message Sent Successfully!',
          description: 'Thank you for contacting us. We will get back to you soon.',
          duration: 5000,
        });

        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
        });
      } else {
        setResult('');
        toast({
          title: 'Error',
          description: 'Failed to send message. Please try again or call us directly.',
          duration: 5000,
        });
      }
    } catch (error) {
      setResult('');
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again or call us directly.',
        duration: 5000,
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Contact Us"
        description="Get in touch with OneSource IT Solutions. Contact us for IT staffing, recruitment, and consulting services. Located in Hyderabad, serving clients across India and globally."
        keywords="contact IT staffing, Hyderabad IT recruitment, OneSource contact, IT consulting contact"
      />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-orange-50 opacity-60"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-purple-400/20 via-pink-400/20 to-orange-400/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Let's start a{' '}
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-orange-500 bg-clip-text text-transparent">
                conversation
              </span>
            </h1>
            <p className="text-xl text-slate-600">
              Get in touch with us to discuss your IT staffing needs
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form and Info Section */}
      <section
        ref={(el) => (sectionsRef.current[0] = el)}
        className="py-24 px-6 bg-white opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Get in touch
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                We're here to help you find the perfect IT talent for your organization. Reach out
                to us through any of the following channels:
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#f9a825]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Address</h3>
                    <p className="text-slate-600 text-sm">
                      Plot No. 44, Geethala Infra,
                      <br />
                      Kakatiya Hills, Madhapur,
                      <br />
                      Hyderabad, Telangana, India
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#f9a825]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Phone</h3>
                    <a
                      href="tel:+91-9121711433"
                      className="text-slate-600 hover:text-[#f9a825] transition-colors duration-200 text-sm"
                    >
                      +91-9121711433
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#f9a825]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                    <a
                      href="mailto:info@onesource.ind.in"
                      className="text-slate-600 hover:text-[#f9a825] transition-colors duration-200 text-sm"
                    >
                      info@onesource.ind.in
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Linkedin className="w-6 h-6 text-[#f9a825]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">LinkedIn</h3>
                    <a
                      href="#"
                      className="text-slate-600 hover:text-[#f9a825] transition-colors duration-200 text-sm"
                    >
                      Connect with us on LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Send us a message</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-slate-900 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#f9a825] focus:border-transparent transition-all duration-200 bg-white"
                      placeholder="Your Name"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-slate-900 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#f9a825] focus:border-transparent transition-all duration-200 bg-white"
                        placeholder="your.email@example.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-slate-900 mb-2">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#f9a825] focus:border-transparent transition-all duration-200 bg-white"
                        placeholder="+91-XXXXXXXXXX"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm font-semibold text-slate-900 mb-2">
                      Service Interested In *
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#f9a825] focus:border-transparent transition-all duration-200 bg-white"
                    >
                      <option value="">Select a service</option>
                      <option value="staff-augmentation">IT Staff Augmentation</option>
                      <option value="permanent-recruitment">Permanent IT Recruitment</option>
                      <option value="contract-to-hire">Contract-to-Hire Staffing</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-slate-900 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#f9a825] focus:border-transparent transition-all duration-200 bg-white"
                      placeholder="Tell us about your staffing needs..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full flex items-center justify-center space-x-2 px-6 py-4 bg-[#f9a825] text-white font-semibold rounded-full hover:bg-[#f57c00] transition-all duration-300 group"
                  >
                    <Send className="w-5 h-5" />
                    <span>Submit</span>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section
        ref={(el) => (sectionsRef.current[1] = el)}
        className="py-24 px-6 bg-slate-50 opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Find us here
            </h2>
          </div>
          <div className="bg-slate-200 rounded-2xl overflow-hidden shadow-lg" style={{ height: '400px' }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3806.3893737883047!2d78.38840431487628!3d17.44619588804637!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb9159c2b1e9e3%3A0x2b1e2b1e2b1e2b1e!2sMadhapur%2C%20Hyderabad%2C%20Telangana!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="OneSource Location"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
